import { useCallback } from 'react';

import { DeserializedUser } from '@api';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTagManager } from '@shared/common/providers/GoogleTagManagerProvider';
import { useFeatureToggles } from '@shared/hooks/useFeatureToggles';
import useLoginWithGivenWallet from '@shared/hooks/useLoginWithGivenWallet';

type UserInfo = { isNewUser: boolean; user: DeserializedUser };

type LoginProps = {
  walletType: 'Passkey' | 'WalletConnect2';
  onLoginCallback?: (userInfo: UserInfo) => void;
};

export const useLogin = () => {
  const { logout } = useAuth();
  const featureToggles = useFeatureToggles();

  const {
    mutateAsync: loginWithWallet,
    isPending,
    variables,
  } = useLoginWithGivenWallet({
    onLogout: logout,
  });
  const isLoggingInWithPasskey = isPending && variables?.signProps.walletType === 'Passkey';
  const isLoggingInWithWalletConnect =
    isPending && (!featureToggles.passkeyAuth || variables?.signProps.walletType === 'EVM');

  const { triggerAuthEvent } = useTagManager();

  const login = useCallback(
    async ({ walletType, onLoginCallback }: LoginProps) => {
      const userInfo = await loginWithWallet({
        signProps: {
          walletType,
          signType: 'SIWE',
          message: 'Confirm authorization in Magic Store with your wallet',
          shouldVerifyChain: true,
          shouldReconnect: true,
        },
      });

      if (userInfo) {
        triggerAuthEvent(userInfo.user.id, {
          isNewUser: userInfo.isNewUser,
        });

        if (onLoginCallback) {
          onLoginCallback(userInfo);
        }
      }
    },
    [loginWithWallet, triggerAuthEvent],
  );

  return {
    login,
    isLogging: isPending,
    isLoggingInWithPasskey,
    isLoggingInWithWalletConnect,
  };
};
